import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { HistoryInvoiceTableRow } from '@/components/history/HistoryInvoiceTableRow'
import { formatSwedishDay } from '@/lib/format'
import { addInvoiceByNumber, linkOptionsFor, removeInvoice, setExternalWhy, setInvoiceLink } from '@/lib/history'
import type { InvoiceLine } from '@/components/history/HistoryInvoiceTableRow'
import type { HistoryInvoiceLink, HistoryWorklistEntry, Invoice, Shipment } from '@/api/types'

interface HistoryInvoicesCardProps {
  invoices: Invoice[]
  shipments: Shipment[]
  entry: HistoryWorklistEntry
  isDisabled: boolean
  onChange: (entry: HistoryWorklistEntry) => void
}

export function HistoryInvoicesCard({ invoices, shipments, entry, isDisabled, onChange }: HistoryInvoicesCardProps): React.ReactElement {
  const [newNumber, setNewNumber] = useState('')
  const lines: InvoiceLine[] = [
    ...invoices.map((invoice): InvoiceLine => ({ kind: 'platform', invoice })),
    ...entry.invoices.map(
      (invoice, index): InvoiceLine => ({ kind: 'add', index, invoice, options: linkOptionsFor(entry, shipments, invoice.link) })
    )
  ]
  const hasLines = lines.length > 0
  const canAdd = newNumber.trim() !== '' && !isDisabled

  const handleLinkChange = (index: number, link: HistoryInvoiceLink): void => {
    onChange(setInvoiceLink(entry, index, link))
  }

  const handleWhyChange = (index: number, why: string): void => {
    onChange(setExternalWhy(entry, index, why))
  }

  const handleRemove = (index: number): void => {
    onChange(removeInvoice(entry, index))
  }

  const handleAdd = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault()

    if (!canAdd) return

    onChange(addInvoiceByNumber(entry, newNumber, formatSwedishDay(new Date())))
    setNewNumber('')
  }

  return (
    <Card className="shadow-none border-border/80">
      <CardHeader>
        <CardTitle>3. Fakturor</CardTitle>
        <CardDescription>
          Fakturorna som finns i plattformen och de handgjorda Capway-fakturor som läggs till. För varje tillagd faktura: välj vilken
          leverans den gällde. Beloppen hämtas från Capway.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {!hasLines && <p className="text-sm text-muted-foreground">Inga fakturor.</p>}
        {hasLines && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-muted/50 text-left text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">Vad händer</th>
                  <th className="px-3 py-2 font-medium">Nummer</th>
                  <th className="px-3 py-2 font-medium">Datum</th>
                  <th className="px-3 py-2 font-medium">Belopp</th>
                  <th className="px-3 py-2 font-medium">Betalning</th>
                  <th className="px-3 py-2 font-medium">Gäller leverans</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {lines.map((line) => (
                  <HistoryInvoiceTableRow
                    key={line.kind === 'platform' ? `inv-${line.invoice.id}` : `add-${line.index}`}
                    line={line}
                    isDisabled={isDisabled}
                    onLinkChange={handleLinkChange}
                    onWhyChange={handleWhyChange}
                    onRemove={handleRemove}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
        <form
          onSubmit={handleAdd}
          className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-border/80 p-3"
        >
          <span className="text-sm">Saknas en handgjord faktura? Skriv Capways fakturanummer:</span>
          <Input
            value={newNumber}
            disabled={isDisabled}
            onChange={(event) => setNewNumber(event.target.value)}
            placeholder="t.ex. 103010"
            className="w-40"
          />
          <Button
            type="submit"
            variant="outline"
            size="sm"
            className="rounded-none"
            disabled={!canAdd}
          >
            Lägg till fakturan
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
